import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useContext } from 'react';
import { AlertContext } from '../../../contexts/alert-context';
import type { ReceivingSession } from '../../../data/receiving';
import { isSessionHeldByOtherTab } from '../../../data/receiving';
import { logUnexpectedApiError } from '../../../helpers/api-errors';
import { receivingSessionsQueryOptions } from '../../../query/query-options';
import type { ReceivingService } from '../../../services/receiving';

// The one cancel refusal the user resolves themselves. Names the single action
// that works instead of "try again" — retrying from this tab fails identically
// for as long as the other tab has the session open.
const SESSION_HELD_BY_OTHER_TAB_MESSAGE =
  'This receiving session is already open in another browser tab. Switch to that tab to cancel it.';

const CANCEL_FAILED_MESSAGE = "Couldn't cancel the receiving session. Please try again.";

/**
 * Shared failure handling for the two Cancel entry points (the session page and
 * the sessions list). Both need the same split, and having it in one place is
 * what keeps the copy from drifting apart:
 *
 * - Another tab holds the session: expected, and the user acts on it in that
 *   tab. A *warning* toast, because addErrorAlert reports its message to
 *   Datadog — which is how a normal outcome came to page us.
 * - Anything else: reportable, with the retry prompt.
 *
 * `label` distinguishes the two entry points in Datadog; both logged the same
 * string before, so a report couldn't say which Cancel had failed.
 *
 * Returns true when the failure was the expected refusal, for callers whose
 * recovery differs (the list keeps its confirm modal open to retry, which is
 * useless when the fix is in another tab).
 */
export function useReportCancelFailure(label: string) {
  const { addErrorAlert, addWarningAlert } = useContext(AlertContext);

  return useCallback(
    (err: unknown): boolean => {
      if (isSessionHeldByOtherTab(err)) {
        addWarningAlert(SESSION_HELD_BY_OTHER_TAB_MESSAGE);

        return true;
      }

      logUnexpectedApiError(label, err);
      addErrorAlert(CANCEL_FAILED_MESSAGE, { cause: err });

      return false;
    },
    [label, addErrorAlert, addWarningAlert]
  );
}

/**
 * Shared cancel handler for a receiving session viewed from the session page.
 * Cancels via the backend, optimistically drops the row from the sessions-list
 * cache (the list page's WebSocket invalidation only runs while it's mounted,
 * so without this the list would briefly show the stale row), then navigates
 * back to the list. Used by both the session-header Cancel and the `failed`
 * screen's Cancel.
 */
export function useCancelSession(storeId: string, service: ReceivingService) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const reportFailure = useReportCancelFailure('receiving: cancel failed (session page)');

  return useCallback(
    async (sessionId: string) => {
      try {
        await service.cancel(storeId, sessionId);
        queryClient.setQueryData<ReceivingSession[]>(
          receivingSessionsQueryOptions(storeId).queryKey,
          (sessions) => sessions?.filter((s) => s.session_id !== sessionId)
        );
        void navigate({ to: '/stores/$storeId/receiving', params: { storeId } });
      } catch (err) {
        reportFailure(err);
      }
    },
    [storeId, service, queryClient, navigate, reportFailure]
  );
}
